import * as PIXI from "pixi.js";
import { rootEventBus, EnumRootEventBus } from "../../../managers/RootEventBus";
import { Coord } from "../../../types/Interface";
import Arc from "../../Arc";
import CompassRenderer from "./CompassRenderer";
import addInteractivity from "../../../utils/addInteractivity";
import limitDistance from "./helpers/limitDistance";
import CompassFactory from "./CompassFactory";
import getAngleBetweenPoints from "../../../utils/math/getAngleBtwPoints";
import getDistanceBtwPoints from "../../../utils/getDistanceBtwPoints";
import getCleanCoord from "../../../utils/coord/getCleanCoord";
import { CustomCursorType } from "../../UI/CustomCursor/CustomCursor";


const TOTAL_H = 360;


export default class Compass {
  private CompassRenderer = new CompassRenderer(TOTAL_H);
  public initialStartCoord!: Coord;
  public initialEndCoord!: Coord;
  public startCoord!: Coord;
  public endCoord!: Coord;
  public radian!: number;
  public isKnobPressed: boolean = false;
  public isNeedleLegPressed: boolean = false;
  public isPencilLegPressed: boolean = false;
  public pStartCoord!: Coord;
  private drawing!: Arc;
  private startRad: number
  private arcColor: number = 0x000000;

  constructor(private getDrawing: (arc: Arc) => void) {
    this.resetValues();
    this.addInteractivities();

    rootEventBus.on(EnumRootEventBus.setPointerMove, (coord: Coord) => this.handlePointerMove(coord));
    rootEventBus.on(EnumRootEventBus.setPointerUp, () => this.handlePointerUp());
  }
  setColor = (color: number) => {
    this.arcColor = color;
    this.CompassRenderer.setPencilColor(color);
    this.CompassRenderer.draw();
  }
  handlePointerUp = () => CompassFactory.handlePointerUp(this);
  private resetValues = () => CompassFactory.resetValues(this);
  private initializeDrawing() {
    this.drawing = new Arc({ color: this.arcColor, lWidth: 3, autoRemove: false, hasCenterPoint: true });
    this.getDrawing(this.drawing);
  }

  private addInteractivities() {
    const getHandleDownFunc = (e: PIXI.FederatedPointerEvent, onHandle: () => void) => {
      this.pStartCoord = getCleanCoord({ x: e.global.x, y: e.global.y });
      this.handleEventMode(false);
      onHandle();
    }
    const handleDownNeedleFunc = (e: PIXI.FederatedPointerEvent) => getHandleDownFunc(e, () => this.isNeedleLegPressed = true);
    const handleDownPencilLegFunc = (e: PIXI.FederatedPointerEvent) => getHandleDownFunc(e, () => {
      this.isPencilLegPressed = true;
      this.CompassRenderer.isLockActive && this.initializeDrawing();
    });
    const handleDownKnobFunc = (e: PIXI.FederatedPointerEvent) => getHandleDownFunc(e, () => {
      this.isKnobPressed = true;
      this.initializeDrawing();
      this.handleLockActTmprary(true);
    })
    const handleDownLockFunc = (e: PIXI.FederatedPointerEvent) => getHandleDownFunc(e, () => {
      this.CompassRenderer.isLockActive = !this.CompassRenderer.isLockActive;
      this.CompassRenderer.draw(this.startCoord, this.endCoord);
      this.handleEventMode(true);
    })

    addInteractivity(this.CompassRenderer.lock, { handleDownFunc: handleDownLockFunc }, CustomCursorType.pointer)
    addInteractivity(this.CompassRenderer.knob, { handleDownFunc: handleDownKnobFunc }, CustomCursorType.grab);
    addInteractivity(this.CompassRenderer.needleLeg, { handleDownFunc: handleDownNeedleFunc }, CustomCursorType.grab)
    addInteractivity(this.CompassRenderer.pencilLeg, { handleDownFunc: handleDownPencilLegFunc }, CustomCursorType.grab)
    addInteractivity(this.CompassRenderer.pencil, { handleDownFunc: handleDownPencilLegFunc }, CustomCursorType.grab)
  }

  handleLockActTmprary(v: boolean) {
    this.CompassRenderer.isLockActiveTmprary = v;
    this.CompassRenderer.draw(this.startCoord, this.endCoord);
  }
  handlePointerMove(coord: Coord) {
    CompassFactory.handlePointerMove(this, coord)
    this.CompassRenderer.draw(this.startCoord, this.endCoord);
  };
  setVisible(v: boolean) {
    this.CompassRenderer.visible = v;
    this.resetValues();
    v ? this.CompassRenderer.draw(this.startCoord, this.endCoord) : this.CompassRenderer.clearAll();
  }
  handleKnobPointerMove(coord: Coord) {
    const startRad = getAngleBetweenPoints(this.initialStartCoord, this.pStartCoord);
    const radStartAndEnd = getAngleBetweenPoints(this.initialStartCoord, this.initialEndCoord);
    const rad = getAngleBetweenPoints(this.initialStartCoord, coord);
    const deltaRad = Number((rad - startRad + radStartAndEnd).toFixed(4));
    const d = getDistanceBtwPoints(this.initialStartCoord, this.initialEndCoord);

    this.endCoord = {
      x: Math.floor(this.startCoord.x + Math.cos(deltaRad) * d),
      y: Math.floor(this.startCoord.y + Math.sin(deltaRad) * d)
    }
    const sRad = getAngleBetweenPoints(this.initialStartCoord, this.initialEndCoord);
    const currRad = getAngleBetweenPoints(this.initialStartCoord, this.endCoord);

    !this.startRad ? this.startRad = sRad : "";

    this.drawing.draw(this.startCoord, d, this.startRad, currRad);
    this.startRad = currRad;
  }
  handleNeedleLegPointerMove(deltaX: number, deltaY: number) {
    this.startCoord = {
      x: Math.max(this.initialStartCoord.x + deltaX, 0),
      y: Math.max(this.initialStartCoord.y + deltaY, 0)
    }
    this.endCoord = {
      x: Math.max(this.initialEndCoord.x + deltaX, 0),
      y: Math.max(this.initialEndCoord.y + deltaY, 0)
    }
  }
  handlePencilLegPointerMove(coord: Coord, deltaX: number, deltaY: number) {
    if (this.CompassRenderer.isLockActive) {
      return this.handleKnobPointerMove(coord);
    };

    const newEndCoord = {
      x: Math.max(this.initialEndCoord.x + deltaX, 0),
      y: Math.max(this.initialEndCoord.y + deltaY, 0)
    }
    this.endCoord = limitDistance(this.startCoord, newEndCoord, TOTAL_H);
    this.startRad = getAngleBetweenPoints(this.startCoord, this.endCoord);
  }
  handleEventMode(v: boolean) {
    [this.CompassRenderer.knob, this.CompassRenderer.needleLeg, this.CompassRenderer.pencilLeg, this.CompassRenderer.pencil, this.CompassRenderer.lock]
      .forEach(elm => elm.eventMode = v ? 'static' : 'none');
  }
}